import type { Command } from 'commander';
import {
	buildUserAgent,
	createApiClient,
	throwForHttpStatus,
	unwrapDataResponse,
	type ApiClient,
} from '@wport/core';
import { CLI_SOURCE, resolveContext } from '../../lib/global-opts';
import { CliError, ExitCode } from '../../lib/errors';
import { dim, printJson, printNdjsonLine, sanitizeForTerminal, sanitizeForTerminalMultiline } from '../../lib/output';
import { getPath, parseFieldsList, pickPaths } from '../../lib/path-utils';
import { mapWithConcurrency } from '@wport/core';
import { readPipedStdin } from '../../lib/io-helpers';
import pc from 'picocolors';

interface ViewFlags {
	field?: string;
	fields?: string;
	batch?: boolean;
	concurrency?: number;
}

const DEFAULT_BATCH_CONCURRENCY = 5;
const MAX_BATCH_CONCURRENCY = 20;

/** One ND-JSON record emitted per enc_id in --batch mode. */
interface BatchResult {
	enc_id: string;
	ok: boolean;
	data?: unknown;
	error?: string;
}

/** Projects a fetched job down to whatever --field / --fields asked for (or the whole job). */
type JobProjector = (job: JobView) => unknown;

/**
 * JobViewVM 是嵌套結構（見 src/modules/jobs/view-models/job-view.vm.ts）。
 * 這裡只列我們顯示時會碰到的欄位，其他欄位走 [k: string]: unknown 保留。
 */
interface JobView {
	company_header_info?: {
		company_name?: string;
		company_icon_url?: string;
		enc_company_id?: string;
		[k: string]: unknown;
	};
	job_description?: string;
	job_info?: {
		job_title?: string;
		area_display?: string;
		salary_display?: string;
		job_feature_display?: string | null;
		experience_display?: string | null;
		[k: string]: unknown;
	};
	job_information?: Record<string, unknown>;
	recruitment_conditions?: Record<string, unknown>;
	benefits?: Record<string, unknown>;
	about_company?: Record<string, unknown> | null;
	application_method?: Record<string, unknown> | null;
	structured_data?: Record<string, unknown> | null;
	[k: string]: unknown;
}

export function registerJobsView(parent: Command): void {
	parent
		.command('view <enc_id>')
		.description('View a single job. Pass "-" to read enc_id from stdin.')
		.option('--field <path>', 'output a single field as a raw value (dotted paths, e.g. job_info.job_title)')
		.option(
			'--fields <list>',
			'output selected fields as a JSON object (comma-separated dotted paths, e.g. job_info.job_title,company_header_info.company_name)'
		)
		.option(
			'--batch',
			'read newline-separated enc_ids from stdin and emit one ND-JSON record per job (requires "-" as the enc_id arg)'
		)
		.option(
			'--concurrency <n>',
			`max parallel requests in --batch mode (default ${DEFAULT_BATCH_CONCURRENCY}, max ${MAX_BATCH_CONCURRENCY})`,
			(v) => Number(v)
		)
		.action(async (encIdArg: string, flags: ViewFlags, command: Command) => {
			const ctx = resolveContext(command);
			if (flags.field && flags.fields) {
				throw new CliError(
					'Use either --field (single raw value) or --fields (JSON object), not both',
					ExitCode.InvalidArgument
				);
			}

			const client = createApiClient({
				baseUrl: ctx.baseUrl,
				locale: ctx.locale,
				timeoutMs: ctx.timeoutMs,
				userAgent: buildUserAgent('wport-cli', __CLI_VERSION__),
				source: CLI_SOURCE,
			});

			if (flags.batch) {
				await runBatchView(encIdArg, flags, client, ctx.timeoutMs);
				return;
			}

			const encId = encIdArg === '-' ? readPipedStdin('view -', { timeoutMs: ctx.timeoutMs }).trim() : encIdArg;
			if (!encId) {
				throw new CliError('enc_id is required', ExitCode.InvalidArgument);
			}

			const { data, error, response } = await client.GET('/api/jobs/{encId}/view', {
				params: { path: { encId } },
			});
			if (!response.ok) throwForHttpStatus(response.status, error);

			const job = unwrapDataResponse<JobView>(data);

			if (flags.fields) {
				// Multi-field projection → JSON object keyed by dotted path. printJson uses
				// JSON.stringify, which escapes control chars to \uXXXX, so employer-controlled
				// string values are safe without extra sanitization (same rationale as the
				// json branch below).
				printJson(pickPaths(job, parseFieldsList(flags.fields)));
				return;
			}

			if (flags.field) {
				const v = getPath(job, flags.field);
				if (v === undefined) {
					throw new CliError(`Field "${flags.field}" not present in response`, ExitCode.InvalidArgument);
				}
				// String values are employer-controlled and printed raw — sanitize. The multiline
				// variant preserves \n (e.g. when --field selects job_description) but still strips
				// every other control char including \r and \t. JSON.stringify already escapes
				// < 0x20 to \uXXXX so non-string paths don't need extra handling.
				const out = typeof v === 'string' ? sanitizeForTerminalMultiline(v) : JSON.stringify(v, null, 2);
				process.stdout.write(out + '\n');
				return;
			}

			if (ctx.format === 'json') {
				printJson(job);
				return;
			}

			renderJobTable(job, encId, ctx.color);
		});
}

/**
 * --batch orchestration: read enc_ids off stdin, fetch each (bounded parallelism),
 * and stream one ND-JSON record per job. One failed enc_id becomes an `ok:false` line
 * rather than aborting the whole run. Output order matches input order.
 */
async function runBatchView(encIdArg: string, flags: ViewFlags, client: ApiClient, timeoutMs: number): Promise<void> {
	if (encIdArg !== '-') {
		throw new CliError('--batch reads enc_ids from stdin; pass "-" as the enc_id argument', ExitCode.InvalidArgument);
	}
	const encIds = parseBatchInput(readPipedStdin('view - --batch', { timeoutMs }));
	if (encIds.length === 0) {
		throw new CliError('No enc_ids found on stdin', ExitCode.InvalidArgument);
	}
	const concurrency = resolveBatchConcurrency(flags.concurrency);
	const project = makeBatchProjector(flags);
	const results = await runBatch(encIds, concurrency, (encId) => fetchJob(client, encId), project);
	for (const record of results) printNdjsonLine(record);
}

async function fetchJob(client: ApiClient, encId: string): Promise<JobView> {
	const { data, error, response } = await client.GET('/api/jobs/{encId}/view', {
		params: { path: { encId } },
	});
	if (!response.ok) throwForHttpStatus(response.status, error);
	return unwrapDataResponse<JobView>(data);
}

/**
 * Pure batch driver (injectable fetcher) so the success/failure-mix behaviour is
 * testable without a live API. Each unit is wrapped so a rejected fetch turns into an
 * `ok:false` record instead of rejecting the whole batch.
 */
async function runBatch(
	encIds: string[],
	concurrency: number,
	fetchOne: (encId: string) => Promise<JobView>,
	project: JobProjector
): Promise<BatchResult[]> {
	return mapWithConcurrency(encIds, concurrency, async (encId): Promise<BatchResult> => {
		try {
			const job = await fetchOne(encId);
			return { enc_id: encId, ok: true, data: project(job) };
		} catch (err) {
			return { enc_id: encId, ok: false, error: err instanceof Error ? err.message : String(err) };
		}
	});
}

function makeBatchProjector(flags: ViewFlags): JobProjector {
	if (flags.fields) {
		const paths = parseFieldsList(flags.fields);
		return (job) => pickPaths(job, paths);
	}
	if (flags.field) {
		const path = flags.field;
		// Missing path → null (keeps every record's shape stable across the batch),
		// unlike single-view --field which errors on a missing path.
		return (job) => getPath(job, path) ?? null;
	}
	return (job) => job;
}

function parseBatchInput(raw: string): string[] {
	return raw
		.split('\n')
		.map((s) => s.trim())
		.filter(Boolean);
}

function resolveBatchConcurrency(raw: number | undefined): number {
	const n = raw ?? DEFAULT_BATCH_CONCURRENCY;
	if (!Number.isInteger(n) || n < 1 || n > MAX_BATCH_CONCURRENCY) {
		throw new CliError(
			`--concurrency must be an integer between 1 and ${MAX_BATCH_CONCURRENCY} (got ${raw})`,
			ExitCode.InvalidArgument
		);
	}
	return n;
}

function renderJobTable(job: JobView, encId: string, color: boolean): void {
	const label = (s: string) => (color ? pc.bold(s) : s);
	// All API string values are employer-controlled; sanitize before printing to defend
	// against terminal escape injection (clear screen, OSC 8 phishing hyperlinks, etc.).
	const s = (v: string | undefined | null): string => (v ? sanitizeForTerminal(v) : '');
	const info = job.job_info ?? {};
	const company = job.company_header_info ?? {};

	const lines: string[] = [];
	if (info.job_title) lines.push(`${label('Title:')}      ${s(info.job_title)}`);
	if (company.company_name) lines.push(`${label('Company:')}    ${s(company.company_name)}`);
	if (info.area_display) lines.push(`${label('Location:')}   ${s(info.area_display)}`);
	if (info.salary_display) lines.push(`${label('Salary:')}     ${s(info.salary_display)}`);
	if (info.job_feature_display) lines.push(`${label('Type:')}       ${s(info.job_feature_display)}`);
	if (info.experience_display) lines.push(`${label('Experience:')} ${s(info.experience_display)}`);
	// encId comes from the CLI arg (user-provided) but pass through sanitize as a defense-in-depth.
	lines.push(dim(`enc_id:     ${s(encId)}`, color));
	if (company.enc_company_id) lines.push(dim(`enc_company_id: ${s(company.enc_company_id)}`, color));
	process.stdout.write(lines.join('\n') + '\n');

	if (job.job_description) {
		process.stdout.write('\n' + label('Description') + '\n');
		// stripHtml only removes tags; sanitize afterwards in case the rich-text source
		// embedded raw escape sequences inside text nodes. Use the multiline variant so the
		// description's paragraph breaks (\n) are preserved while \r / \t / other controls
		// are still stripped.
		process.stdout.write(renderDescription(job.job_description) + '\n');
	}

	process.stdout.write(
		'\n' +
			dim(
				'Tip: use --output json (or --field <dotted.path>, e.g. --field job_info.salary_display) for scripting.',
				color
			) +
			'\n'
	);
}

/**
 * 後端 job_description 可能含 HTML（rich text）；CLI 終端機列印時剝掉 tag。
 * 不做完整 HTML 解析 —— 只把 tag 拿掉、& 實體做最常見的還原。
 */
function stripHtml(s: string): string {
	return s
		.replace(/<\/?(p|br|div|li|h[1-6])[^>]*>/gi, '\n')
		.replace(/<[^>]+>/g, '')
		.replace(/&nbsp;/g, ' ')
		.replace(/&amp;/g, '&')
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.replace(/&quot;/g, '"')
		.replace(/&#39;/g, "'")
		.replace(/\n{3,}/g, '\n\n')
		.trim();
}

function renderDescription(html: string): string {
	return sanitizeForTerminalMultiline(stripHtml(html));
}

// Internal helpers exposed for tests. Not part of the public CLI API.
export const __test__ = {
	stripHtml,
	getPath,
	renderDescription,
	runBatch,
	makeBatchProjector,
	parseBatchInput,
	resolveBatchConcurrency,
};
